import { useState, useEffect, useCallback, useRef } from 'react';
import { tokenStore } from '../lib/tokenStore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FileSignature, Search, Download, Filter, Eye, XCircle, CheckCircle2, Clock, AlertTriangle, Camera, Pencil, ArrowLeftRight
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import PapiervertragFotoDialog from '@/components/PapiervertragFotoDialog';
import VertragBerichtigenDialog from '@/components/VertragBerichtigenDialog';
import ZahlerwechselDialog from '@/components/ZahlerwechselDialog';
import AdressenNachtragen from '@/components/adressen/AdressenNachtragen';
import AdresseKarte from '@/components/adressen/AdresseKarte';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ ...tokenStore.getAuthHeaders(), 'Content-Type': 'application/json' });
function fmt(v) { return (v || 0).toFixed(2).replace('.', ','); }

const STATUS_MAP = {
  aktiv: { label: 'Aktiv', color: '#22C55E', icon: CheckCircle2 },
  gekuendigt: { label: 'Gekündigt', color: '#EF4444', icon: XCircle },
  abgelaufen: { label: 'Abgelaufen', color: '#6B7280', icon: Clock },
  pausiert: { label: 'Pausiert', color: '#F59E0B', icon: AlertTriangle },
};

export default function VertraegePage() {
  const [vertraege, setVertraege] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedVertrag, setSelectedVertrag] = useState(null);
  const [fotoOffen, setFotoOffen] = useState(false);
  // Vertrag ändern (Leitung, 10.10.2026): Berichtigung und Zahlerwechsel — der Server lässt nur admin/manager zu
  const [berichtigenVertrag, setBerichtigenVertrag] = useState(null);
  const [zahlerwechselVertrag, setZahlerwechselVertrag] = useState(null);
  const { user, userPermissions, isAdminPermissions } = useAuth();
  const darfAdresse = isAdminPermissions || !!(userPermissions || {})['leads.edit'];
  const leitung = user?.role === 'admin' || user?.role === 'manager';
  // Zahler wechseln darf, wer Verträge abschließt (wie der Server); Berichtigen nur die Leitung
  const darfZahler = leitung || isAdminPermissions || !!userPermissions?.['verkauf.create_vertrag'];
  const navigate = useNavigate();
  // Kündigen und Bankverbindung ändern (30.10.2026): eigener Dialog statt window.confirm; die Mitglieder-Seite führt mit
  // /vertraege?vertrag=<id>&aktion=kuendigen hierher — Kündigen läuft nur noch über den Vertrag
  const [kuendigenVertrag, setKuendigenVertrag] = useState(null);
  const [kuendigenBusy, setKuendigenBusy] = useState(false);
  const [kuendigenFehler, setKuendigenFehler] = useState('');
  const [bankVertrag, setBankVertrag] = useState(null);
  const [suchParams, setSuchParams] = useSearchParams();
  const deepLink = useRef(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const url = statusFilter ? `${API}/api/vertraege?status=${statusFilter}` : `${API}/api/vertraege`;
      const res = await fetch(url, { headers: getHeaders() });
      if (res.ok) {
        const d = await res.json();
        setVertraege(d.vertraege || []);
        setLoading(false);
        return d.vertraege || [];
      }
    } catch (e) {  }
    setLoading(false);
    return null;
  }, [statusFilter]);

  // Nach einer Änderung: Liste neu laden und das Overlay mit dem frischen Vertrag zeigen (falls es noch offen ist).
  // Der Server liefert nur einen Kurzsatz (kurz); die volle Zeile kommt aus der neu geladenen Liste.
  const nachAenderung = useCallback(async (kurz) => {
    const liste = await fetchData();
    const frisch = kurz?.id ? (liste || []).find(x => x.id === kurz.id) : null;
    setSelectedVertrag(prev => (prev && kurz?.id && prev.id === kurz.id) ? (frisch || { ...prev, ...kurz }) : prev);
  }, [fetchData]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Sprung von der Mitglieder-Seite: den Vertrag öffnen und die gewünschte Aktion anbieten
  useEffect(() => {
    const id = suchParams.get('vertrag');
    if (!id || deepLink.current || !vertraege.length) return;
    deepLink.current = true;
    const v = vertraege.find(x => x.id === id);
    if (!v) { toast.error('Der Vertrag wurde nicht gefunden'); return; }
    setSelectedVertrag(v);
    const aktion = suchParams.get('aktion');
    if (aktion === 'kuendigen' && v.status === 'aktiv') setKuendigenVertrag(v);
    if (aktion === 'bank' && v.status === 'aktiv') setBankVertrag(v);
    const rest = new URLSearchParams(suchParams); rest.delete('vertrag'); rest.delete('aktion');
    setSuchParams(rest, { replace: true });
  }, [vertraege, suchParams, setSuchParams]);

  const cancelVertrag = (v) => { setKuendigenFehler(''); setKuendigenVertrag(v); };
  const kuendigenBestaetigt = async () => {
    if (!kuendigenVertrag) return;
    setKuendigenBusy(true); setKuendigenFehler('');
    try {
      const res = await fetch(`${API}/api/vertraege/${kuendigenVertrag.id}/cancel`, { method: 'PATCH', headers: getHeaders() });
      if (res.ok) { toast.success('Vertrag und Mitgliedschaft gekündigt'); setKuendigenVertrag(null); fetchData(); setSelectedVertrag(null); }
      else { const d = await res.json().catch(() => ({})); setKuendigenFehler(typeof d.detail === 'string' ? d.detail : 'Der Vertrag konnte nicht gekündigt werden'); }
    } catch { setKuendigenFehler('Netzwerkfehler — bitte noch einmal versuchen'); }
    setKuendigenBusy(false);
  };

  const filtered = vertraege.filter(v => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (v.vorname + ' ' + v.nachname).toLowerCase().includes(q) || v.vertragsnummer?.toLowerCase().includes(q) || v.email?.toLowerCase().includes(q);
  });

  const stats = {
    total: vertraege.length,
    aktiv: vertraege.filter(v => v.status === 'aktiv').length,
    gekuendigt: vertraege.filter(v => v.status === 'gekuendigt' || v.status === 'gekündigt').length,
  };

  return (
    <div className="space-y-5 fade-in" data-testid="vertraege-page">
      <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Vertragsübersicht</h1>
          <p className="text-muted-foreground text-xs sm:text-sm">{stats.total} Verträge · {stats.aktiv} aktiv</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setFotoOffen(true)} className="flex items-center gap-2 border border-border text-xs font-bold px-4 py-2.5 rounded-xl hover:bg-muted transition-colors" data-testid="btn-papier-foto">
            <Camera className="w-4 h-4" /> Papiervertrag fotografieren
          </button>
          <button onClick={() => navigate('/verkauf')} className="flex items-center gap-2 bg-marke text-white text-xs font-bold px-4 py-2.5 rounded-xl hover:bg-marke-dunkel transition-colors" data-testid="btn-new-vertrag">
            <FileSignature className="w-4 h-4" /> Neuer Vertrag
          </button>
        </div>
        <PapiervertragFotoDialog offen={fotoOffen} onClose={() => setFotoOffen(false)} onFertig={() => fetchData()} />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Gesamt', value: stats.total, color: '#3B82F6' },
          { label: 'Aktiv', value: stats.aktiv, color: '#22C55E' },
          { label: 'Gekündigt', value: stats.gekuendigt, color: '#EF4444' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-border/60 p-3 text-center">
            <div className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</div>
            <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Adressen nachtragen (23.10.2026): nur sichtbar, wenn bei laufenden Verträgen eine Adresse fehlt */}
      {darfAdresse && <AdressenNachtragen />}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Name, Nr. oder E-Mail..." data-testid="search-vertraege"
            className="w-full h-9 pl-9 pr-3 bg-white border border-border/60 rounded-lg text-sm" />
        </div>
        <div className="flex gap-1.5">
          {['', 'aktiv', 'gekuendigt'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} data-testid={`filter-${s || 'all'}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${statusFilter === s ? 'bg-marke text-white' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
              {s === '' ? 'Alle' : s === 'aktiv' ? 'Aktiv' : 'Gekündigt'}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <div key={`skel-${i}`} className="h-16 bg-muted animate-pulse rounded-lg" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <FileSignature className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Keine Verträge gefunden</p>
        </div>
      ) : (
        <Card className="border-border/60 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Nr.</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Mitglied</th>
                  <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Tarif</th>
                  <th className="text-center px-3 py-2.5 font-medium text-muted-foreground">Laufzeit</th>
                  <th className="text-right px-3 py-2.5 font-medium text-muted-foreground">4-Wo.-Beitrag</th>
                  <th className="text-center px-3 py-2.5 font-medium text-muted-foreground">Status</th>
                  <th className="text-center px-3 py-2.5 font-medium text-muted-foreground">Extras</th>
                  <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(v => {
                  const st = STATUS_MAP[v.status] || STATUS_MAP['aktiv'];
                  const StIcon = st.icon;
                  return (
                    <tr key={v.id} className="border-b border-border/30 hover:bg-muted/10 transition-colors" data-testid={`row-${v.id}`}>
                      <td className="px-4 py-3 font-mono text-xs font-medium">{v.vertragsnummer}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-xs">{v.vorname} {v.nachname}</div>
                        <div className="text-[10px] text-muted-foreground">{v.email}</div>
                      </td>
                      <td className="px-3 py-3 text-xs">{v.plan_name}</td>
                      <td className="px-3 py-3 text-center text-xs">{v.duration_label}</td>
                      <td className="px-3 py-3 text-right font-bold text-xs">{fmt(v.monthly_price)} €</td>
                      <td className="px-3 py-3 text-center">
                        <Badge className="text-[9px] gap-1" style={{ backgroundColor: st.color + '15', color: st.color, borderColor: st.color + '30' }}>
                          <StIcon className="w-3 h-3" /> {st.label}
                        </Badge>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div className="flex justify-center gap-1">
                          {v.getraenke_flat && <span className="w-5 h-5 rounded bg-blue-50 text-blue-500 text-[8px] font-bold flex items-center justify-center" title="Getränke-Flat">G</span>}
                          {v.betreuter_einstieg && <span className="w-5 h-5 rounded bg-red-50 text-red-500 text-[8px] font-bold flex items-center justify-center" title="Betreuter Einstieg">E</span>}
                          {v.gym_sauna && <span className="w-5 h-5 rounded bg-amber-50 text-amber-600 text-[8px] font-bold flex items-center justify-center" title="Gym & Sauna">GS</span>}
                          {v.geschwister_rabatt && <span className="w-5 h-5 rounded bg-violet-50 text-violet-500 text-[8px] font-bold flex items-center justify-center" title="Geschwisterrabatt">GR</span>}
                          {v.tag1_angebot && <span className="w-5 h-5 rounded bg-emerald-50 text-emerald-500 text-[8px] font-bold flex items-center justify-center" title="Tag 1 Angebot">T1</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button onClick={() => setSelectedVertrag(v)} className="p-1.5 rounded-md hover:bg-muted" title="Details" data-testid={`view-${v.id}`}>
                            <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                          </button>
                          <button onClick={() => window.open(`${API}/api/vertraege/${v.id}/pdf`, '_blank')} className="p-1.5 rounded-md hover:bg-muted" title="PDF" data-testid={`pdf-${v.id}`}>
                            <Download className="w-3.5 h-3.5 text-muted-foreground" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Detail Overlay */}
      {selectedVertrag && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setSelectedVertrag(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="vertrag-detail">
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold">{selectedVertrag.vorname} {selectedVertrag.nachname}</h2>
                  <p className="text-xs text-muted-foreground font-mono">{selectedVertrag.vertragsnummer}</p>
                </div>
                <Badge style={{ backgroundColor: (STATUS_MAP[selectedVertrag.status]?.color || '#6B7280') + '15', color: STATUS_MAP[selectedVertrag.status]?.color || '#6B7280' }}>
                  {STATUS_MAP[selectedVertrag.status]?.label || selectedVertrag.status}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <Detail label="Tarif" value={selectedVertrag.plan_name} />
                <Detail label="Laufzeit" value={selectedVertrag.duration_label} />
                <Detail label="4-Wo.-Beitrag" value={`${fmt(selectedVertrag.monthly_price)} €`} bold />
                <Detail label="Vertragsbeginn" value={selectedVertrag.vertragsbeginn} />
                <Detail label="Vertragsende" value={selectedVertrag.vertragsende} />
                <Detail label="Kategorie" value={{ kinder: 'Kind/Jugend', family: 'Family (Kind + Elternteil)' }[selectedVertrag.category] || 'Erwachsene'} />
                <Detail label="E-Mail" value={selectedVertrag.email} />
                <Detail label="Telefon" value={selectedVertrag.telefon} />
              </div>
              {selectedVertrag.lead_id && <AdresseKarte key={selectedVertrag.lead_id} leadId={selectedVertrag.lead_id} darf={darfAdresse} eingebettet />}

              {(selectedVertrag.getraenke_flat || selectedVertrag.betreuter_einstieg || selectedVertrag.gym_sauna || selectedVertrag.geschwister_rabatt || selectedVertrag.checkup_kontingent || selectedVertrag.tag1_angebot) && (
                <div>
                  <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Extras</p>
                  <div className="flex gap-2 flex-wrap">
                    {selectedVertrag.getraenke_flat && <Badge variant="secondary" className="text-[10px]">Getränke-Flat</Badge>}
                    {selectedVertrag.betreuter_einstieg && <Badge variant="secondary" className="text-[10px]">Betreuter Einstieg{selectedVertrag.betreuter_einstieg_betrag ? ` (${selectedVertrag.betreuter_einstieg_betrag} €)` : ' (frei)'}</Badge>}
                    {selectedVertrag.gym_sauna && <Badge variant="secondary" className="text-[10px]">Gym & Sauna{selectedVertrag.gym_sauna_inklusive ? ' (inklusive)' : ''}</Badge>}
                    {selectedVertrag.geschwister_rabatt && <Badge variant="secondary" className="text-[10px]">Geschwisterrabatt −25 %</Badge>}
                    {selectedVertrag.checkup_kontingent ? <Badge variant="secondary" className="text-[10px]">{selectedVertrag.checkup_kontingent} Check-Ups inklusive</Badge> : null}
                    {selectedVertrag.tag1_angebot && <Badge variant="secondary" className="text-[10px]">Tag-1-Angebot{selectedVertrag.betreuter_einstieg_ersparnis ? ` (${selectedVertrag.betreuter_einstieg_ersparnis} € gespart)` : ''}</Badge>}
                  </div>
                </div>
              )}

              <div>
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">SEPA</p>
                <div className="text-xs space-y-0.5">
                  <p><span className="text-muted-foreground">Kontoinhaber:</span> {selectedVertrag.kontoinhaber}</p>
                  <p><span className="text-muted-foreground">IBAN:</span> {selectedVertrag.iban_maskiert || selectedVertrag.iban}</p>
                  {selectedVertrag.mandat?.referenz && <p><span className="text-muted-foreground">Mandat:</span> {selectedVertrag.mandat.referenz}</p>}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                <button onClick={() => window.open(`${API}/api/vertraege/${selectedVertrag.id}/pdf`, '_blank')}
                  className="flex-1 flex items-center justify-center gap-1.5 border border-border/60 text-sm font-medium py-2.5 rounded-xl hover:bg-muted transition-colors" data-testid="detail-pdf">
                  <Download className="w-4 h-4" /> PDF
                </button>
                {selectedVertrag.status === 'aktiv' && leitung && (
                  <button onClick={() => setBerichtigenVertrag(selectedVertrag)} title="Vorname, Nachname oder Geburtsdatum richtigstellen"
                    className="flex-1 flex items-center justify-center gap-1.5 border border-border/60 text-sm font-medium py-2.5 rounded-xl hover:bg-muted transition-colors" data-testid="detail-berichtigen">
                    <Pencil className="w-4 h-4" /> Berichtigen
                  </button>
                )}
                {selectedVertrag.status === 'aktiv' && darfZahler && (
                  <button onClick={() => setZahlerwechselVertrag(selectedVertrag)} title="Eine andere Person zahlt: neues SEPA-Mandat am iPad oder per QR"
                    className="flex-1 flex items-center justify-center gap-1.5 border border-border/60 text-sm font-medium py-2.5 rounded-xl hover:bg-muted transition-colors" data-testid="detail-zahlerwechsel">
                    <ArrowLeftRight className="w-4 h-4" /> Zahler wechseln
                  </button>
                )}
                {selectedVertrag.status === 'aktiv' && darfZahler && (
                  <button onClick={() => setBankVertrag(selectedVertrag)} title="Dieselbe Person zahlt von einem anderen Konto: neues SEPA-Mandat am iPad oder per QR"
                    className="flex-1 flex items-center justify-center gap-1.5 border border-border/60 text-sm font-medium py-2.5 rounded-xl hover:bg-muted transition-colors" data-testid="detail-bankverbindung">
                    <ArrowLeftRight className="w-4 h-4" /> Bankverbindung ändern
                  </button>
                )}
                {selectedVertrag.status === 'aktiv' && (
                  <button onClick={() => cancelVertrag(selectedVertrag)}
                    className="flex-1 flex items-center justify-center gap-1.5 border border-red-200 text-red-600 text-sm font-medium py-2.5 rounded-xl hover:bg-red-50 transition-colors" data-testid="detail-cancel">
                    <XCircle className="w-4 h-4" /> Kündigen
                  </button>
                )}
                <button onClick={() => setSelectedVertrag(null)} className="px-4 py-2.5 border border-border/60 rounded-xl text-sm hover:bg-muted transition-colors">
                  Schließen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Die Dialoge stehen ausserhalb des Overlays: ihre Klicks duerfen nicht ueber den Portal-Baum
          zum Overlay-Hintergrund hochlaufen und es schliessen. Sie halten ihren Vertrag selbst. */}
      <VertragBerichtigenDialog vertragId={berichtigenVertrag?.id} offen={!!berichtigenVertrag}
        onClose={() => setBerichtigenVertrag(null)}
        onFertig={(kurz) => { setBerichtigenVertrag(null); nachAenderung(kurz); }} />
      <ZahlerwechselDialog vertragId={zahlerwechselVertrag?.id} vertrag={zahlerwechselVertrag} offen={!!zahlerwechselVertrag}
        onClose={() => setZahlerwechselVertrag(null)}
        onFertig={(kurz) => nachAenderung(kurz)} />
      {/* Bankverbindung ändern: derselbe Vorgang wie der Zahlerwechsel, dieselbe Person vorbelegt, neues Mandat ist Pflicht */}
      <ZahlerwechselDialog bankverbindung vertragId={bankVertrag?.id} vertrag={bankVertrag} offen={!!bankVertrag}
        onClose={() => setBankVertrag(null)}
        onFertig={(kurz) => nachAenderung(kurz)} />
      <Dialog open={!!kuendigenVertrag} onOpenChange={(o) => { if (!o && !kuendigenBusy) setKuendigenVertrag(null); }}>
        <DialogContent className="max-w-md" data-testid="kuendigen-dialog">
          <DialogHeader><DialogTitle>Vertrag kündigen</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              Vertrag <b>{kuendigenVertrag?.vertragsnummer}</b> von <b>{kuendigenVertrag?.full_name || `${kuendigenVertrag?.vorname || ''} ${kuendigenVertrag?.nachname || ''}`.trim()}</b> wirklich kündigen?
            </p>
            <p className="text-xs text-muted-foreground">Das beendet Vertrag und Mitgliedschaft zusammen, meldet das Mitglied in der App ab und legt die Aufgabe „Kündigung nachfassen“ an. Das lässt sich hier nicht zurücknehmen.</p>
            {kuendigenFehler && <p className="text-xs rounded-lg bg-red-50 text-red-700 px-3 py-2" data-testid="kuendigen-fehler">{kuendigenFehler}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setKuendigenVertrag(null)} disabled={kuendigenBusy}>Abbrechen</Button>
              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={kuendigenBestaetigt} disabled={kuendigenBusy} data-testid="kuendigen-bestaetigen">Vertrag kündigen</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Detail({ label, value, bold }) {
  return (
    <div>
      <p className="text-[10px] text-muted-foreground">{label}</p>
      <p className={`text-sm ${bold ? 'font-bold text-marke' : 'font-medium'}`}>{value || '—'}</p>
    </div>
  );
}
